// We can not useState or useRef in a server component, which is why we are
// extracting this part out into it's own file with 'use client' on top
import { ReactNode } from "react";
import { MutationCache, QueryClient, QueryClientProvider } from "@tanstack/react-query";
// import { ReactQueryDevtools } from "@tanstack/react-query-devtools";

import { authService } from "@/features/auth/service/auth.service";
import { ApiError, toastError } from "@/lib/errors";

declare module "@tanstack/react-query" {
  interface Register {
    mutationMeta: {
      /** Set false to silence the global error toast for a mutation with no onError. */
      toast?: false;
      /** Fallback toast copy when the backend sent no message ("Couldn't <verb> <object>."). */
      errorMessage?: string;
    };
  }
}

function makeQueryClient() {
  return new QueryClient({
    // CONVENTION: a mutation that defines its own onError owns ALL its error UX - the global
    // handler stays silent. Queries deliberately have NO global error toast: background
    // refetches (window focus, polls) must never toast; reads render errors inline.
    mutationCache: new MutationCache({
      onError: (error, _variables, _context, mutation) => {
        if (mutation.options.onError) return;
        if (mutation.meta?.toast === false) return;
        // Session already torn down - the single "Session expired" toast covers it.
        if (error instanceof ApiError && error.status === 401 && !authService.isAuthenticated()) return;
        toastError(error, mutation.meta?.errorMessage ?? "Something went wrong. Please try again.");
      },
    }),
    defaultOptions: {
      queries: {
        // With SSR, we usually want to set some default staleTime
        // above 0 to avoid refetching immediately on the client
        staleTime: 10 * 1000,
      },
    },
  });
}

let browserQueryClient: QueryClient | undefined = undefined;

function getQueryClient() {
  if (typeof window === "undefined") {
    // Server: always make a new query client
    return makeQueryClient();
  } else {
    // Browser: make a new query client if we don't already have one
    // This is very important so we don't re-make a new client if React
    // suspends during the initial render. This may not be needed if we
    // have a suspense boundary BELOW the creation of the query client
    if (!browserQueryClient) browserQueryClient = makeQueryClient();
    return browserQueryClient;
  }
}

type Props = {
  children: ReactNode;
};

export function QueryProviders({ children }: Props) {
  // NOTE: Avoid useState when initializing the query client if you don't
  //       have a suspense boundary between this and the code that may
  //       suspend because React will throw away the client on the initial
  //       render if it suspends and there is no boundary
  const queryClient = getQueryClient();

  return (
    <QueryClientProvider client={queryClient}>
      {children}
      {/* <ReactQueryDevtools initialIsOpen={false} /> */}
    </QueryClientProvider>
  );
}
